import { faArrowRightLong } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ComboboxParsedItemGroup, Stack } from '@mantine/core';
import { useAppSelector } from '@vision/ui/hooks';
import { ReportChartGroupCompareItemType, ReportsDetailGraphicSchemaValues } from '@vision/ui/interfaces';
import { getReportChartCompareKey } from '@vision/ui/pages/ReportsDetailGraphic/utils';
import { selectedChartGroupItemSelector } from '@vision/ui/store';
import { translateErrorMessage } from '@vision/ui/utils';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportsDetailGraphicConfigurationValueHeader } from '../../../ReportsDetailGraphicConfigurationValueHeader';
import { ReportsDetailGraphicConfigurationValueCompareSelect } from '../ReportsDetailGraphicConfigurationValueCompareSelect';

interface ReportsDetailGraphicConfigurationValueCompareProps {
  compareSelectItems: ComboboxParsedItemGroup[];
  disabled: boolean;
  showIcon?: boolean;
  labelKey?: string;
}

export function ReportsDetailGraphicConfigurationValueCompare({
  compareSelectItems,
  disabled,
  showIcon = true,
  labelKey,
}: ReportsDetailGraphicConfigurationValueCompareProps) {
  const { t } = useTranslation(['page-reports', 'translation']);
  const selectedChartGroupItem = useAppSelector(selectedChartGroupItemSelector);
  const formik = useFormikContext<ReportsDetailGraphicSchemaValues>();
  const isCardChart1 = selectedChartGroupItem?.type === 'card-chart-1';
  const isCompareMultiple = isCardChart1 ? false : selectedChartGroupItem.config?.compareMultiple;

  useEffect(() => {
    if (!isCardChart1 || formik.values.selectedCompare.length <= 1) {
      return;
    }

    const nextValues = formik.values.selectedCompare.slice(0, 1);
    const removedValue = formik.values.selectedCompare.slice(1);
    const removedFormikValues = getReportChartCompareKey(removedValue).reduce(
      (acc: Record<string, any>, item: string): Record<string, any> => {
        return {
          ...acc,
          [item]: [],
        };
      },
      {},
    );

    formik.setValues({
      ...formik.values,
      selectedCompare: nextValues,
      selectedCompareCriteria: {
        ...formik.values.selectedCompareCriteria,
        ...removedFormikValues,
      },
    });
  }, [formik, isCardChart1]);

  const handleSelectCompare = (values: ReportChartGroupCompareItemType[]) => {
    const nextValues = isCompareMultiple ? values : values.slice(0, 1);

    // If the selected chart group element does not allow multiple comparisons and
    // any of the selected ones are already selected, terminate the operation.
    if (!isCompareMultiple && nextValues.some((item) => formik.values.selectedCompare.includes(item))) {
      return;
    }

    // Two compare value can be selected. So we find the values that are removed and subtract them from the formic values.
    // Filter the removed values from the selected values.
    const removedValue = formik.values.selectedCompare.filter((item) => !nextValues.includes(item));

    // Creating deprecated Formik values.
    const removedFormikValues = getReportChartCompareKey(removedValue).reduce(
      (acc: Record<string, any>, item: string): Record<string, any> => {
        return {
          ...acc,
          [item]: [],
        };
      },
      {},
    );

    // Reset and update the Formik form
    formik.setValues({
      ...formik.values,
      selectedCompare: nextValues,
      selectedCompareCriteria: {
        ...formik.values.selectedCompareCriteria,
        ...removedFormikValues,
      },
    });
  };

  const CompareRendererComponent =
    formik.values.selectedCompare.length > 0 && selectedChartGroupItem.config.compareRendererComponent;

  const showAsterisk = selectedChartGroupItem.config?.compareHeader?.showAsterisk ?? true;

  return (
    <Stack gap={10}>
      <ReportsDetailGraphicConfigurationValueHeader
        label={t(labelKey || selectedChartGroupItem?.config?.compareHeader?.labelKey || 'page-reports:compareXAxis')}
        icon={
          showIcon
            ? (selectedChartGroupItem?.config?.compareHeader?.showIcon ?? <FontAwesomeIcon icon={faArrowRightLong} />)
            : null
        }
      />

      <ReportsDetailGraphicConfigurationValueCompareSelect
        compareSelectItems={compareSelectItems}
        value={formik.values.selectedCompare}
        disabled={disabled}
        onChange={handleSelectCompare}
        multiple={isCompareMultiple}
        error={translateErrorMessage(formik, 'selectedCompare')}
        clearable={!selectedChartGroupItem?.config?.defaultCompares}
        showAsterisk={showAsterisk}
      />

      {CompareRendererComponent && <CompareRendererComponent compareItemTypes={formik.values.selectedCompare} />}
    </Stack>
  );
}
